import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, Compass, Users, Calendar, Flame, Store, User, Plus, Bell, Search,
  LogOut, LayoutDashboard, Sparkles, Bookmark, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/explore", label: "Explore", icon: Compass },
  { to: "/communities", label: "Communities", icon: Users },
  { to: "/events", label: "Events", icon: Calendar },
  { to: "/challenges", label: "Challenges", icon: Flame },
  { to: "/marketplace", label: "Marketplace", icon: Store },
];

function CreateMenu({ children }) {
  const nav = useNavigate();
  const items = [
    { to: "/create/community", label: "Community", icon: Users, d: "Start a space for your passion" },
    { to: "/create/event", label: "Event", icon: Calendar, d: "Host a meetup or workshop" },
    { to: "/create/challenge", label: "Challenge", icon: Flame, d: "Launch an engagement loop" },
    { to: "/create/project", label: "Project", icon: Sparkles, d: "Showcase what you made" },
    { to: "/create/listing", label: "Marketplace Listing", icon: Store, d: "Sell a creation or service" },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-2 rounded-2xl" data-testid="create-menu">
        <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#55708F]">Create something</p>
        {items.map((it) => (
          <DropdownMenuItem key={it.to} onClick={() => nav(it.to)} className="rounded-xl p-3 cursor-pointer focus:bg-[#FF6B00]/8" data-testid={`create-${it.label.toLowerCase().split(" ")[0]}-item`}>
            <span className="h-9 w-9 rounded-lg bg-[#FF6B00]/10 grid place-items-center mr-3"><it.icon className="h-4.5 w-4.5 text-[#FF6B00]" /></span>
            <span className="flex flex-col"><span className="font-semibold text-[#071126]">{it.label}</span><span className="text-xs text-[#071126]/55">{it.d}</span></span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const load = async () => {
    try {
      const r = await api.get("/notifications");
      if (seen.current != null && r.data.items[0] && r.data.items[0].id !== seen.current) {
        const fresh = r.data.items.find((n) => !n.read);
        if (fresh) toast(fresh.text, { icon: "🔔" });
      }
      seen.current = r.data.items[0]?.id ?? seen.current;
      setData(r.data);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const markRead = async () => { await api.post("/notifications/read"); load(); };
  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o && data.unread) markRead(); }}>
      <DropdownMenuTrigger asChild>
        <button className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-[#071126]/5 transition-colors pa-focus" data-testid="notifications-bell" aria-label="Notifications">
          <Bell className="h-5 w-5 text-[#071126]" />
          {data.unread > 0 && <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-[#FF6B00] text-white text-[10px] font-bold grid place-items-center">{data.unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-[#071126]/8 font-semibold text-[#071126]">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-6 text-sm text-center text-[#071126]/50">You're all caught up.</p>}
          {data.items.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b border-[#071126]/5 flex gap-3 ${!n.read ? "bg-[#FF6B00]/5" : ""}`}>
              <span className="h-2 w-2 rounded-full bg-[#FF6B00] mt-1.5 flex-shrink-0" style={{ opacity: n.read ? 0.2 : 1 }} />
              <p className="text-sm text-[#071126]/80">{n.text}</p>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchBar({ mobile }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); };
  return (
    <form onSubmit={submit} className={`relative ${mobile ? "w-full" : "w-64 xl:w-80"}`}>
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#55708F]" />
      <input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search passions, people, events…"
        className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-[#071126]/10 text-sm text-[#071126] placeholder:text-[#55708F]/70 focus:border-[#FF6B00]/50 pa-focus"
        data-testid="global-search-input"
      />
    </form>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mSearch, setMSearch] = useState(false);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FFFDF8]/90 backdrop-blur-md border-b border-[#071126]/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Logo to="/home" />
          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "text-[#071126]/70 hover:text-[#071126] hover:bg-[#071126]/5"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:block min-w-0"><SearchBar /></div>
            <button className="md:hidden h-10 w-10 grid place-items-center rounded-full hover:bg-[#071126]/5 shrink-0" onClick={() => setMSearch((v) => !v)} data-testid="mobile-search-toggle"><Search className="h-5 w-5 text-[#071126]" /></button>
            <NotificationBell />
            <CreateMenu>
              <button className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#FF6B00] text-white font-semibold text-sm shadow-[0_6px_18px_rgba(255,107,0,0.28)] hover:bg-[#e05e00] transition-all pa-focus shrink-0" data-testid="create-button">
                <Plus className="h-4 w-4" /> Create
              </button>
            </CreateMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="pa-focus rounded-full shrink-0" data-testid="user-menu-trigger">
                  <img src={fileUrl(user?.avatar)} alt={user?.name} className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-sm" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl p-1.5" data-testid="user-menu">
                <div className="px-3 py-2">
                  <p className="font-semibold text-[#071126] truncate">{user?.name}</p>
                  <p className="text-xs text-[#071126]/55 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/me")} className="rounded-lg cursor-pointer" data-testid="menu-profile"><User className="h-4 w-4 mr-2" />My Passion</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard")} className="rounded-lg cursor-pointer" data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />Builder Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/saved")} className="rounded-lg cursor-pointer" data-testid="menu-saved"><Bookmark className="h-4 w-4 mr-2" />Saved</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="rounded-lg cursor-pointer text-[#c25400]" data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {mSearch && <div className="md:hidden px-4 pb-3"><SearchBar mobile /></div>}
      </header>

      <main className="pb-24 lg:pb-10 min-h-[70vh]">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#071126]/8 shadow-[0_-4px_20px_rgba(7,17,38,0.06)]">
        <div className="grid grid-cols-5 items-center h-16 px-2">
          {[NAV[0], NAV[1]].map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-[#FF6B00]" : "text-[#55708F]"}`} data-testid={`bottom-nav-${n.label.toLowerCase()}`}>
              <n.icon className="h-5 w-5" />{n.label}
            </NavLink>
          ))}
          <div className="flex justify-center">
            <CreateMenu>
              <button className="h-13 w-13 -mt-6 h-14 w-14 rounded-2xl bg-[#FF6B00] text-white grid place-items-center shadow-[0_8px_20px_rgba(255,107,0,0.4)] pa-focus" data-testid="bottom-nav-create"><Plus className="h-6 w-6" /></button>
            </CreateMenu>
          </div>
          <NavLink to="/communities" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-[#FF6B00]" : "text-[#55708F]"}`} data-testid="bottom-nav-communities"><Users className="h-5 w-5" />Groups</NavLink>
          <NavLink to="/me" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-[#FF6B00]" : "text-[#55708F]"}`} data-testid="bottom-nav-profile"><User className="h-5 w-5" />Profile</NavLink>
        </div>
      </nav>
    </div>
  );
}
